import type { ReactNode } from 'react';
import type { DocumentoStatus, HabitaCliente, HabitaDocumento, PastaStatus } from '../../models';
import {
  getPastaStatusBadgeClass,
  getPastaStatusLabel,
} from '../../habitaStatusPresentation';
import { classNames } from './classNames';
import { habitaControlBorderClass } from './HabitaSurface';

const pastaStatusSizeClasses = {
  xs: 'px-2 py-0.5 text-xs',
  sm: 'px-2.5 py-0.5 text-xs',
};

function docStatusSegmentClass(status: DocumentoStatus): string {
  if (status === 'validado') return 'bg-emerald-500';
  if (status === 'reprovado' || status === 'expirado') return 'bg-red-500';
  if (status === 'revisao' || status === 'em_analise') return 'bg-amber-500';
  if (status === 'desconhecido') return 'bg-slate-400';
  return 'bg-muted-foreground/35';
}

function normalizeDocumentCount(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value ?? 0));
}

function buildDocumentSummarySegments({
  reprovados,
  total,
  validados,
}: {
  reprovados?: number;
  total?: number;
  validados?: number;
}): DocumentoStatus[] {
  const totalCount = normalizeDocumentCount(total);
  const validCount = Math.min(totalCount, normalizeDocumentCount(validados));
  const rejectedCount = Math.min(totalCount - validCount, normalizeDocumentCount(reprovados));
  const pendingCount = Math.max(0, totalCount - validCount - rejectedCount);

  return [
    ...Array.from({ length: validCount }, () => 'validado' as DocumentoStatus),
    ...Array.from({ length: rejectedCount }, () => 'reprovado' as DocumentoStatus),
    ...Array.from({ length: pendingCount }, () => 'em_analise' as DocumentoStatus),
  ];
}

export function PastaStatusBadge({
  status,
  children,
  className,
  size = 'sm',
}: {
  status: PastaStatus;
  children?: ReactNode;
  className?: string;
  size?: keyof typeof pastaStatusSizeClasses;
}) {
  return (
    <span
      className={classNames(
        'inline-flex shrink-0 rounded-md border font-instrument font-medium',
        pastaStatusSizeClasses[size],
        getPastaStatusBadgeClass(status),
        className
      )}
    >
      {children ?? getPastaStatusLabel(status)}
    </span>
  );
}

export function OrigemBadge({ origem }: { origem: HabitaCliente['origem'] }) {
  return (
    <span
      className={`inline-flex rounded-md border ${habitaControlBorderClass} bg-muted px-2 py-0.5 font-instrument text-xs text-textSecondary`}
    >
      {origem}
    </span>
  );
}

export function DocumentProgress({
  documentos,
  className,
  reprovados,
  total,
  validados,
}: {
  documentos?: HabitaDocumento[];
  className?: string;
  reprovados?: number;
  total?: number;
  validados?: number;
}) {
  const docs = documentos ?? [];
  const summarySegments = docs.length > 0
    ? []
    : buildDocumentSummarySegments({ reprovados, total, validados });
  const resolvedTotal = docs.length > 0 ? docs.length : summarySegments.length;

  return (
    <div className={classNames('flex gap-1', className)} aria-label={`${resolvedTotal} documentos`}>
      {docs.length > 0
        ? docs.map((documento) => (
            <span
              key={documento.id}
              className={`h-1.5 flex-1 rounded-full ${docStatusSegmentClass(documento.status)}`}
              title={`${documento.nome}: ${documento.status}`}
            />
          ))
        : summarySegments.map((status, index) => (
            <span
              key={`${status}-${index}`}
              className={`h-1.5 flex-1 rounded-full ${docStatusSegmentClass(status)}`}
              title={`Documento ${index + 1}: ${status}`}
            />
          ))}
    </div>
  );
}
